/**
 * Painel dos Indicadores ANS — o ano corrente de relance, e tudo clicável.
 *
 * Em cima, cada indicador com o público dividido em "em dia" / "a 1 passo" /
 * "longe": clicar num pedaço (da barra ou da legenda) abre, num modal, a lista
 * daquele grupo com contato, facilidade e CSV. Depois, os idosos cruzados
 * generalista × especialista (2.2 e 1.9 juntos), com cada célula clicável. Por
 * fim, os críticos para CUIDADO (não para a nota), também abrindo em modal.
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, Users, X } from "lucide-react";
import api from "../../services/api";
import { T, MONO, InfoTip, Card, Botao, fmtInt } from "./ui";
import { Pendentes } from "./IdssBase";

const COR = { em_dia: "#27ae60", perto: "#f39c12", longe: "#e74c3c" };
const num = (v, casas = 1) => (v === null || v === undefined ? "—"
  : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const dataBr = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const TIP_PAINEL = "O ano corrente de relance, pelas regras da ANS válidas para este ano. Em cada indicador, o público está dividido em: EM DIA (já conta para a ANS neste ano), A 1 PASSO (falta pouco ou já fez antes: é quem converte mais fácil, comece por aqui) e LONGE. Clique em qualquer pedaço para ver quem está nele. A projeção supõe o mesmo ritmo até dezembro; o 'mesmo ponto do ano anterior' usa a mesma regra, para comparar ritmo.";
const TIP_CRITICOS = "Casos em que a falta do exame ou da consulta é risco para a pessoa, não só para a nota: vão para a equipe de cuidado, com a lista e o contato. Não entram em nenhuma mensagem automática.";

// Modal da lista: abre por cima do painel (não perde o lugar), fecha no X, fora ou Esc.
function ModalLista({ lista, onFechar }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onFechar(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onFechar]);
  if (!lista) return null;
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "min(1100px, 100%)",
        maxHeight: "90vh", overflow: "auto", padding: 16, boxShadow: "0 12px 40px rgba(0,0,0,.25)" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -28 }}>
          <button onClick={onFechar} title="Fechar (Esc)" style={{ border: "none", background: "none", cursor: "pointer", color: T.inkSoft }}><X size={18} /></button>
        </div>
        <Pendentes codigo={lista.codigo} url={lista.url} titulo={lista.titulo} />
      </div>
    </div>
  );
}

function Barra({ i, abrir }) {
  const partes = [["em_dia", i.pct_em_dia], ["perto", i.pct_perto], ["longe", i.pct_longe]];
  const clicar = (k) => abrir({ codigo: `${i.codigo}_${k}`, titulo: `${i.nome} · ${i.rotulos[k]}`,
    url: `/indicadores-ans/painel/lista/${i.codigo}?faixa=${k}` });
  return (
    <div>
      <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "#eef1f4" }}>
        {partes.map(([k, p]) => p > 0 && (
          <div key={k} onClick={() => clicar(k)} title={`${i.rotulos[k]}: ${num(p)}%. Clique para ver a lista`}
            style={{ width: `${p}%`, background: COR[k], cursor: "pointer" }} />
        ))}
      </div>
      <div style={{ display: "grid", gap: 2, marginTop: 6 }}>
        {partes.map(([k, p]) => (
          <button key={k} onClick={() => clicar(k)} title="Ver a lista"
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.inkSoft, border: "none", background: "none",
              padding: "2px 0", cursor: "pointer", textAlign: "left", minHeight: 32, lineHeight: "14px" }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: COR[k], flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{i.rotulos[k]}</span>
            <span style={{ fontFamily: MONO, color: T.azul, textDecoration: "underline" }}>{fmtInt(i[k])}</span>
            <span style={{ fontFamily: MONO, width: 44, textAlign: "right" }}>{num(p)}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CartaoIndicador({ i, ano, abrir }) {
  const abaixo = i.resultado !== null && i.meta !== null && i.resultado < i.meta;
  const ant = i.mesmo_ponto_ano_anterior;
  const piorQueAnt = ant !== null && i.resultado !== null && i.resultado < ant;
  return (
    // Todos os cartões com a mesma estrutura: título em 2 linhas fixas, bloco de
    // números com altura fixa e a barra presa ao rodapé, para alinhar lado a lado.
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, background: "#fff",
      display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, minHeight: 36 }}>
        <span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft, marginTop: 2 }}>{i.codigo}</span>
        <b style={{ fontSize: 13, color: T.ink, flex: 1, lineHeight: "18px", display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden" }} title={i.nome}>{i.nome}</b>
        {i.ajuda_base && <InfoTip w={320} texto={i.ajuda_base} />}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "8px 0 2px" }}>
        <span style={{ fontSize: 26, fontWeight: 700, fontFamily: MONO, color: COR.em_dia }}>{num(i.pct_em_dia)}%</span>
        <span style={{ fontSize: 12, color: T.inkSoft }}>em dia · {fmtInt(i.publico)} no público</span>
      </div>
      <div style={{ fontSize: 12, color: abaixo ? T.risco : T.verde, marginBottom: 2 }}>
        Resultado {num(i.resultado, i.casas)} · meta {num(i.meta, i.casas)}{i.codigo === "M.1" ? "%" : ""}
        {i.nota !== null && i.nota !== undefined && <span style={{ color: T.inkSoft }}> · nota {num(i.nota, 2)}</span>}
      </div>
      <div style={{ fontSize: 11, color: T.inkSoft, marginBottom: 8, minHeight: 46, lineHeight: "15px" }}>
        {i.projecao !== null && <>no ritmo atual, fecha {ano} em <b style={{ color: i.projecao < i.meta ? T.risco : T.verde }}>{num(i.projecao, i.casas)}</b>. </>}
        {ant !== null && <>Mesmo ponto de {ano - 1}: <b style={{ color: piorQueAnt ? T.risco : T.verde }}>{num(ant, i.casas)}</b>{piorQueAnt ? " (ritmo pior)" : ""}.</>}
      </div>
      <div style={{ marginTop: "auto" }}><Barra i={i} abrir={abrir} /></div>
    </div>
  );
}

// Idosos: generalista (0 / 1 / 2+) × especialista (com / sem) — 2.2 e 1.9 na mesma tela.
const TOM = { "0|com": "#fde2cc", "0|sem": "#fbd5d5", "1|com": "#fff4cf", "1|sem": "#fff4cf", "2+|com": "#d1f2e0", "2+|sem": "#d1f2e0" };
const LEITURA = {
  "0|com": "usa o plano e nunca passou pelo clínico: o melhor público",
  "0|sem": "sem consulta no ano",
  "1|com": "falta 1 com generalista",
  "1|sem": "falta 1 com generalista",
  "2+|com": "em dia no 2.2",
  "2+|sem": "em dia no 2.2",
};

function Idosos({ m, ano, abrir }) {
  if (!m) return null;
  const celula = (g, e) => {
    const k = `${g}|${e}`;
    const n = m.celulas[k] || 0;
    return (
      <td key={k} onClick={() => n && abrir({ codigo: `idosos_${g}_${e}`, url: `/indicadores-ans/painel/lista/idosos?faixa=${encodeURIComponent(k)}`,
        titulo: `Idosos com ${g} consulta(s) com generalista, ${e === "com" ? "com" : "sem"} especialista em ${ano}` })}
        title={n ? "Ver a lista" : ""}
        style={{ background: TOM[k], padding: "8px 10px", border: "2px solid #fff", cursor: n ? "pointer" : "default", verticalAlign: "top", minWidth: 150 }}>
        <div style={{ fontSize: 20, fontWeight: 700, fontFamily: MONO, color: T.ink }}>{fmtInt(n)}</div>
        <div style={{ fontSize: 11, color: T.inkSoft }}>{num(m.publico ? (100 * n) / m.publico : null)}% · {LEITURA[k]}</div>
      </td>
    );
  };
  return (
    <div style={{ marginTop: 12, border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: T.ink }}>Idosos: consultas com generalista × especialista em {ano}</b>
        <InfoTip w={380} texto={m.ajuda} />
        <span style={{ fontSize: 12, color: T.inkSoft }}>{fmtInt(m.publico)} idosos · clique numa célula para ver quem está nela</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={{ fontSize: 11, color: T.inkSoft, textAlign: "left", padding: "4px 8px" }}>Consultas com generalista</th>
            <th style={{ fontSize: 11, color: T.inkSoft, textAlign: "left", padding: "4px 8px" }}>Foi a especialista</th>
            <th style={{ fontSize: 11, color: T.inkSoft, textAlign: "left", padding: "4px 8px" }}>Não foi a especialista</th>
          </tr></thead>
          <tbody>
            {["0", "1", "2+"].map((g) => (
              <tr key={g}>
                <td style={{ fontSize: 13, fontWeight: 600, color: T.ink, padding: "4px 8px" }}>{g}</td>
                {celula(g, "com")}{celula(g, "sem")}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 8 }}>
        Na carteira: {fmtInt(m.consultas_generalista)} consultas com generalista e {fmtInt(m.consultas_especialista)} com especialista;
        razão <b style={{ color: m.razao < m.meta_razao ? T.risco : T.verde }}>{num(m.razao, 3)}</b> (meta {num(m.meta_razao, 2)}).
        {m.faltam_para_razao > 0 && <> Para chegar à meta, faltam cerca de <b>{fmtInt(m.faltam_para_razao)}</b> consultas com generalista.</>}
      </div>
    </div>
  );
}

function Criticos({ itens, abrir }) {
  return (
    <div style={{ marginTop: 14, border: `1px solid #f5c6cb`, borderRadius: 10, background: "#fffafa", padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <AlertTriangle size={16} color={T.risco} />
        <b style={{ fontSize: 14, color: T.ink }}>Críticos para cuidado</b>
        <InfoTip w={340} texto={TIP_CRITICOS} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        {itens.map((c) => (
          <div key={c.codigo} style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
              <span style={{ fontSize: 12, color: T.ink, fontWeight: 600, flex: 1 }}>{c.titulo}</span>
              <InfoTip w={340} texto={c.ajuda} />
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "6px 0" }}>
              <span style={{ fontSize: 22, fontWeight: 700, fontFamily: MONO, color: T.risco }}>{fmtInt(c.total)}</span>
              <span style={{ fontSize: 11, color: T.inkSoft }}>{num(c.pct)}% de {fmtInt(c.publico)}</span>
            </div>
            {c.total > 0 && (
              <Botao pequeno onClick={() => abrir({ codigo: c.codigo, titulo: c.titulo, url: `/indicadores-ans/painel/criticos/${c.codigo}` })}>
                <Users size={12} /> Ver lista
              </Botao>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PainelIndicadores() {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(false);
  const [lista, setLista] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get("/indicadores-ans/painel").then((r) => { if (vivo) setD(r.data); }).catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; };
  }, []);
  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: T.ink }}>Painel {d?.ano || ""} · como estamos e quem chamar</b>
        <InfoTip w={400} texto={TIP_PAINEL} />
        {d && <span style={{ fontSize: 12, color: T.inkSoft }}>
          até {dataBr(d.ate)} ({d.dias_passados} de {d.dias_ano} dias do ano) · regras do ano-base {d.ano_fichas} · contas médicas até {dataBr(d.ultima_conta)}
        </span>}
      </div>
      {!d ? (
        <p style={{ fontSize: 13, color: T.inkSoft }}>{erro ? "Não foi possível montar o painel." : "Montando o painel… leva alguns segundos."}</p>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 10, marginTop: 10 }}>
            {d.indicadores.map((i) => <CartaoIndicador key={i.codigo} i={i} ano={d.ano} abrir={setLista} />)}
          </div>
          <Idosos m={d.idosos} ano={d.ano} abrir={setLista} />
          <Criticos itens={d.criticos} abrir={setLista} />
        </>
      )}
      <ModalLista lista={lista} onFechar={() => setLista(null)} />
    </Card>
  );
}
